/**
 * "Make Your Plan" (sections/wm-make-your-plan.liquid, page.plan-fitter-and-faster) — day-length toggle, live
 * macro/price totals, the swap dialog, and "Confirm Meal Plan" add-to-cart.
 *
 * This file used to be ~900 lines of jQuery covering THREE unrelated pages (a collection/PLP filter+sort+load-more
 * system, several legacy subscription/quick-add "tweaks", and this page's own logic) — none of which actually ran,
 * since neither jQuery nor the magnificPopup/flickity plugins it depended on are loaded anywhere in this theme
 * (confirmed: no reference to either in layout/theme.liquid or any bundled vendor script). Every "$(...)" call
 * would have thrown immediately. Rewritten in vanilla JS with only what this page's own markup actually needs.
 *
 * State lives entirely in each slot's own "data-*" attributes (see snippets/wm-myp-daily-menu-product.liquid) —
 * this script reads/writes those directly rather than keeping a parallel JS model, so the DOM is always the
 * single source of truth for what's in the plan.
 */
(() => {
  const root = document.querySelector('[data-wm-myp]');
  if (!root) return;

  // Matches the "plus" icon markup snippets/wm-myp-daily-menu-product.liquid renders for an empty slot, so a
  // slot cleared back to empty via the remove button looks identical to one server-rendered empty on page load.
  const PLACEHOLDER_ICON_SVG =
    '<svg width="22" height="22" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M12 5v14M5 12h14" stroke="currentColor" stroke-width="2" stroke-linecap="round"/></svg>';

  const picker = root.querySelector('[data-wm-myp-picker]');
  const pickerCurrent = picker.querySelector('[data-wm-myp-picker-current]');
  const pickerCurrentImage = picker.querySelector('[data-wm-myp-picker-current-image]');
  const pickerCurrentTitle = picker.querySelector('[data-wm-myp-picker-current-title]');
  let activeSlot = null;

  const formatMoney = (amountInCents) =>
    new Intl.NumberFormat('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(amountInCents / 100);

  /**
   * Recomputes every day's own macro totals, then the grand total price across only the days currently visible in
   * the plan (the 5-day toggle hides the tail days outright — see setPlanLength below — so a hidden day's own
   * "hidden" attribute doubles as "excluded from the total" with nothing extra to keep in sync).
   */
  function updateTotals() {
    let totalPriceCents = 0;

    root.querySelectorAll('[data-wm-myp-day]').forEach((dayEl) => {
      let protein = 0;
      let carbohydrate = 0;
      let fattotal = 0;
      let priceCents = 0;

      dayEl.querySelectorAll('[data-wm-myp-slot]').forEach((slotEl) => {
        protein += parseFloat(slotEl.dataset.protein) || 0;
        carbohydrate += parseFloat(slotEl.dataset.carbohydrate) || 0;
        fattotal += parseFloat(slotEl.dataset.fattotal) || 0;
        priceCents += parseFloat(slotEl.dataset.price) || 0;
      });

      dayEl.querySelector('[data-wm-myp-day-protein]').textContent = `${protein.toFixed(0)}G P`;
      dayEl.querySelector('[data-wm-myp-day-carbs]').textContent = `${carbohydrate.toFixed(0)}G C`;
      dayEl.querySelector('[data-wm-myp-day-fats]').textContent = `${fattotal.toFixed(0)}G F`;

      if (!dayEl.hidden) {
        totalPriceCents += priceCents;
      }
    });

    root.querySelectorAll('[data-wm-myp-total-price]').forEach((el) => {
      el.textContent = formatMoney(totalPriceCents);
    });
  }

  /**
   * "days" is 5 or 7. Rather than the previous hardcoded "day 6 and day 7" indices (which broke the moment a
   * merchant configured anything other than exactly 7 days), this hides whichever days fall after the chosen
   * length, by each day's own "data-day-index" — correct for any number of configured days, and switches the
   * displayed plan with no page reload since it's just a DOM attribute flip.
   */
  function setPlanLength(days) {
    root.querySelectorAll('[data-wm-myp-day-toggle-option]').forEach((option) => {
      const isActive = option.dataset.days === String(days);
      option.classList.toggle('is-active', isActive);
      option.setAttribute('aria-checked', isActive ? 'true' : 'false');
    });

    root.querySelectorAll('[data-wm-myp-day]').forEach((dayEl) => {
      const dayIndex = parseInt(dayEl.dataset.dayIndex, 10);
      dayEl.hidden = dayIndex > days;
    });

    updateTotals();
  }

  function resetSlot(slotEl) {
    slotEl.dataset.price = '0';
    slotEl.dataset.protein = '0';
    slotEl.dataset.carbohydrate = '0';
    slotEl.dataset.fattotal = '0';
    slotEl.dataset.calories = '0';
    slotEl.dataset.id = '0';
    slotEl.dataset.variantId = '0';
    slotEl.dataset.image = '';
    slotEl.dataset.title = '';

    slotEl.classList.add('wm-myp-slot--empty');
    slotEl.querySelector('[data-wm-myp-slot-title]').textContent = '';
    slotEl.querySelector('[data-wm-myp-slot-calories]').textContent = '';
    slotEl.querySelector('[data-wm-myp-slot-protein]').textContent = '0G P';
    slotEl.querySelector('[data-wm-myp-slot-carbs]').textContent = '0G C';
    slotEl.querySelector('[data-wm-myp-slot-fats]').textContent = '0G F';

    const imageHolder = slotEl.querySelector('[data-wm-myp-slot-image]');
    if (imageHolder.tagName === 'IMG') {
      const placeholder = document.createElement('span');
      placeholder.className = 'wm-myp-slot__placeholder';
      placeholder.dataset.wmMypSlotImage = '';
      placeholder.setAttribute('aria-hidden', 'true');
      placeholder.innerHTML = PLACEHOLDER_ICON_SVG;
      imageHolder.replaceWith(placeholder);
    }

    const removeButton = slotEl.querySelector('[data-wm-myp-remove]');
    if (removeButton) removeButton.hidden = true;

    const macros = slotEl.querySelector('[data-wm-myp-slot-macros]');
    if (macros) macros.hidden = true;

    const emptyMessage = slotEl.querySelector('[data-wm-myp-slot-empty-message]');
    if (emptyMessage) emptyMessage.hidden = false;

    setSlotAction(slotEl, 'Add');
  }

  function applyProductToSlot(slotEl, sourceEl) {
    slotEl.dataset.price = sourceEl.dataset.price;
    slotEl.dataset.protein = sourceEl.dataset.protein;
    slotEl.dataset.carbohydrate = sourceEl.dataset.carbohydrate;
    slotEl.dataset.fattotal = sourceEl.dataset.fattotal;
    slotEl.dataset.calories = sourceEl.dataset.calories;
    slotEl.dataset.id = sourceEl.dataset.id;
    slotEl.dataset.variantId = sourceEl.dataset.variantId;
    slotEl.dataset.image = sourceEl.dataset.image;
    slotEl.dataset.title = sourceEl.dataset.title;

    slotEl.classList.remove('wm-myp-slot--empty');
    slotEl.querySelector('[data-wm-myp-slot-title]').textContent = sourceEl.dataset.title;
    slotEl.querySelector('[data-wm-myp-slot-calories]').textContent = `${sourceEl.dataset.calories} CALORIES`;
    slotEl.querySelector('[data-wm-myp-slot-protein]').textContent = `${sourceEl.dataset.protein}G P`;
    slotEl.querySelector('[data-wm-myp-slot-carbs]').textContent = `${sourceEl.dataset.carbohydrate}G C`;
    slotEl.querySelector('[data-wm-myp-slot-fats]').textContent = `${sourceEl.dataset.fattotal}G F`;

    const imageHolder = slotEl.querySelector('[data-wm-myp-slot-image]');
    if (imageHolder.tagName === 'IMG') {
      imageHolder.src = sourceEl.dataset.image;
    } else {
      const image = document.createElement('img');
      image.className = 'wm-myp-slot__image';
      image.dataset.wmMypSlotImage = '';
      image.src = sourceEl.dataset.image;
      image.alt = sourceEl.dataset.title;
      image.loading = 'lazy';
      imageHolder.replaceWith(image);
    }

    const removeButton = slotEl.querySelector('[data-wm-myp-remove]');
    if (removeButton) removeButton.hidden = false;

    const macros = slotEl.querySelector('[data-wm-myp-slot-macros]');
    if (macros) macros.hidden = false;

    const emptyMessage = slotEl.querySelector('[data-wm-myp-slot-empty-message]');
    if (emptyMessage) emptyMessage.hidden = true;

    setSlotAction(slotEl, 'Swap');
  }

  /**
   * Switches a slot's trailing button between "Add" (+ a plus icon, for an empty slot) and "Swap" (+ the swap
   * icon) — both icons are always in the markup (snippets/wm-myp-daily-menu-product.liquid), one hidden, so this
   * only ever toggles which is hidden rather than rebuilding icon markup here.
   */
  function setSlotAction(slotEl, label) {
    const actionLabel = slotEl.querySelector('[data-wm-myp-slot-action-label]');
    if (actionLabel) actionLabel.textContent = label;

    const addIcon = slotEl.querySelector('[data-wm-myp-slot-add-icon]');
    if (addIcon) addIcon.hidden = label !== 'Add';

    const swapIcon = slotEl.querySelector('[data-wm-myp-slot-swap-icon]');
    if (swapIcon) swapIcon.hidden = label === 'Add';
  }

  function openPickerFor(slotEl) {
    activeSlot = slotEl;
    const mealType = slotEl.dataset.mealType;

    picker.querySelectorAll('[data-wm-myp-picker-pool]').forEach((pool) => {
      pool.classList.toggle('is-active', pool.dataset.wmMypPickerPool === mealType);
    });

    const isFilled = slotEl.dataset.variantId && slotEl.dataset.variantId !== '0';
    pickerCurrent.hidden = !isFilled;
    if (isFilled) {
      pickerCurrentImage.src = slotEl.dataset.image;
      pickerCurrentTitle.textContent = slotEl.dataset.title;
    }

    picker.showModal();
  }

  root.addEventListener('click', (event) => {
    const dayToggleOption = event.target.closest('[data-wm-myp-day-toggle-option]');
    if (dayToggleOption) {
      setPlanLength(dayToggleOption.dataset.days);
      return;
    }

    const openPickerTrigger = event.target.closest('[data-wm-myp-open-picker]');
    if (openPickerTrigger) {
      openPickerFor(openPickerTrigger.closest('[data-wm-myp-slot]'));
      return;
    }

    const removeTrigger = event.target.closest('[data-wm-myp-remove]');
    if (removeTrigger) {
      resetSlot(removeTrigger.closest('[data-wm-myp-slot]'));
      updateTotals();
      return;
    }

    const pickTrigger = event.target.closest('[data-wm-myp-pick]');
    if (pickTrigger && activeSlot) {
      applyProductToSlot(activeSlot, pickTrigger.closest('[data-wm-myp-slot]'));
      updateTotals();
      picker.close();
      activeSlot = null;
      return;
    }

    if (event.target.closest('[data-wm-myp-picker-close]')) {
      picker.close();
    }
  });

  // Native <dialog> reports a click on its own backdrop as a click on the <dialog> element itself.
  picker.addEventListener('click', (event) => {
    if (event.target === picker) picker.close();
  });

  root.querySelector('[data-wm-myp-confirm]')?.addEventListener('click', confirmPlan);

  async function confirmPlan() {
    const itemsByVariant = new Map();
    const noteLines = [];

    root.querySelectorAll('[data-wm-myp-day]').forEach((dayEl) => {
      if (dayEl.hidden) return;

      const dayTitles = [`\r\n --> Plan ${dayEl.dataset.dayTitle} <-- \r\n`];

      dayEl.querySelectorAll('[data-wm-myp-slot]').forEach((slotEl) => {
        const variantId = slotEl.dataset.variantId;
        if (!variantId || variantId === '0') return;

        itemsByVariant.set(variantId, (itemsByVariant.get(variantId) || 0) + 1);
        dayTitles.push(slotEl.dataset.title);
      });

      if (dayTitles.length > 1) noteLines.push(dayTitles.join('\r\n '));
    });

    if (itemsByVariant.size === 0) return;

    const items = Array.from(itemsByVariant, ([id, quantity]) => ({ id, quantity }));
    const cartDrawer = document.getElementById('cart-drawer');
    const cartDrawerSectionId = cartDrawer?.closest('[id^="shopify-section-"]')?.id.replace('shopify-section-', '');

    const addResponse = await fetch(`${window.Shopify.routes.root}cart/add.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ items, sections: cartDrawerSectionId }),
    });
    if (!addResponse.ok) return;
    const addResult = await addResponse.json();

    await fetch(`${window.Shopify.routes.root}cart/update.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ note: noteLines.join('') }),
    });

    const cart = await (await fetch(`${window.Shopify.routes.root}cart.js`)).json();
    cart.sections = addResult.sections;

    document.documentElement.dispatchEvent(
      new CustomEvent('cart:change', { bubbles: true, detail: { baseEvent: 'variant:add', cart } })
    );

    cartDrawer?.show?.();
  }

  updateTotals();
})();
